import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ApiServiceBase } from 'src/app/Services/api.service.base';
import { Busyable } from 'src/app/shared/editors/busyable';

enum BlockedNotificationChannels {
  None = 0,
  Email = 1,
  Sms = 2,
  MobileNotification = 4
}

interface NotificationPolicyTemplate {
  TemplateType: number;
  TemplateTypeName: string;
  TemplateDescription: string;
  Category: number;
  CategoryName: string;
  HasPolicy: boolean;
  InheritsCategoryPolicy: boolean;
  BlockedChannels: number;
  PolicyBlockedChannels: number;
  BlockEmail: boolean;
  BlockSms: boolean;
  BlockMobileNotification: boolean;
}

interface NotificationPolicyCategory {
  Category: number;
  CategoryName: string;
  HasPolicy: boolean;
  BlockedChannels: number;
  BlockEmail: boolean;
  BlockSms: boolean;
  BlockMobileNotification: boolean;
  Templates: NotificationPolicyTemplate[];
}

@Component({
  selector: 'app-notificationpolicies',
  templateUrl: './notificationpolicies.component.html',
  styleUrls: ['./notificationpolicies.component.scss'],
  standalone: false
})
export class NotificationPoliciesComponent implements OnInit {
  public categories: NotificationPolicyCategory[] = [];
  public Busyable = new Busyable();
  public loading = false;
  public saving = false;
  public isDirty = false;

  constructor(private apiService: ApiServiceBase, private toastService: ToastrService) {
  }

  ngOnInit(): void {
    this.loadPolicies();
  }

  public loadPolicies(): void {
    this.loading = true;
    this.Busyable.Loading();
    this.apiService.Get<NotificationPolicyCategory[]>('organization/notificationtemplatepolicies')
      .then(result => {
        this.categories = result ?? [];
        this.loading = false;
        this.isDirty = false;
        this.Busyable.StopLoading();
      })
      .catch(() => {
        this.loading = false;
        this.Busyable.StopLoading();
        this.toastService.error('Could not load notification policies');
      });
  }

  public setCategoryChannel(category: NotificationPolicyCategory, channel: BlockedNotificationChannels, checked: boolean): void {
    category.BlockedChannels = this.setFlag(category.BlockedChannels, channel, checked);
    category.HasPolicy = category.BlockedChannels !== BlockedNotificationChannels.None;
    this.applyFlags(category, category.BlockedChannels);

    for (const template of category.Templates) {
      template.BlockedChannels = category.BlockedChannels | template.PolicyBlockedChannels;
      template.InheritsCategoryPolicy = category.BlockedChannels !== BlockedNotificationChannels.None;
      this.applyFlags(template, template.BlockedChannels);
    }

    this.isDirty = true;
  }

  public setTemplateChannel(template: NotificationPolicyTemplate, channel: BlockedNotificationChannels, checked: boolean): void {
    const category = this.categories.find(x => x.Category === template.Category);
    template.PolicyBlockedChannels = this.setFlag(template.PolicyBlockedChannels, channel, checked);
    template.BlockedChannels = (category?.BlockedChannels ?? BlockedNotificationChannels.None) | template.PolicyBlockedChannels;
    template.HasPolicy = template.PolicyBlockedChannels !== BlockedNotificationChannels.None;
    template.InheritsCategoryPolicy = false;
    this.applyFlags(template, template.BlockedChannels);
    this.isDirty = true;
  }

  public isCategoryChannelBlocked(template: NotificationPolicyTemplate, channel: BlockedNotificationChannels): boolean {
    const category = this.categories.find(x => x.Category === template.Category);
    return ((category?.BlockedChannels ?? BlockedNotificationChannels.None) & channel) === channel;
  }

  public clearTemplateOverride(template: NotificationPolicyTemplate): void {
    template.HasPolicy = false;
    template.InheritsCategoryPolicy = false;
    const category = this.categories.find(x => x.Category === template.Category);
    if (category?.HasPolicy) {
      template.InheritsCategoryPolicy = true;
      template.BlockedChannels = category.BlockedChannels;
    }
    else {
      template.BlockedChannels = BlockedNotificationChannels.None;
    }
    template.PolicyBlockedChannels = BlockedNotificationChannels.None;
    this.applyFlags(template, template.BlockedChannels);
    this.isDirty = true;
  }

  public OnSave(): void {
    this.saving = true;
    this.Busyable.Busy();
    this.apiService.Put<NotificationPolicyCategory[]>('organization/notificationtemplatepolicies/bulk', this.getPolicyRequests()).then(result => {
      this.categories = result ?? [];
      this.saving = false;
      this.isDirty = false;
      this.Busyable.StopBusy();
      this.toastService.success('Notification policy updated');
    }).catch(() => {
      this.saving = false;
      this.Busyable.StopBusy();
      this.toastService.error('Could not update notification policy');
      this.loadPolicies();
    });
  }

  private getPolicyRequests(): any[] {
    const requests: any[] = [];

    for (const category of this.categories) {
      if (category.HasPolicy) {
        requests.push({
          Category: category.Category,
          TemplateType: null,
          HasPolicy: true,
          BlockedChannels: category.BlockedChannels
        });
      }

      for (const template of category.Templates) {
        if (template.HasPolicy) {
          requests.push({
            Category: template.Category,
            TemplateType: template.TemplateType,
            HasPolicy: true,
            BlockedChannels: template.PolicyBlockedChannels
          });
        }
      }
    }

    return requests;
  }

  private setFlag(value: number, flag: BlockedNotificationChannels, checked: boolean): number {
    return checked ? (value | flag) : (value & ~flag);
  }

  private applyFlags(target: { BlockedChannels: number, BlockEmail: boolean, BlockSms: boolean, BlockMobileNotification: boolean }, blockedChannels: number): void {
    target.BlockEmail = (blockedChannels & BlockedNotificationChannels.Email) === BlockedNotificationChannels.Email;
    target.BlockSms = (blockedChannels & BlockedNotificationChannels.Sms) === BlockedNotificationChannels.Sms;
    target.BlockMobileNotification = (blockedChannels & BlockedNotificationChannels.MobileNotification) === BlockedNotificationChannels.MobileNotification;
  }
}
